import React, { useCallback, useMemo } from 'react';
import { FlashList } from '@shopify/flash-list';
import { FlatList, StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { TextButton } from 'design-system/TextButton';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectActiveAccountsWithHidden } from 'reducers/selectors';
import { sortAccountsByType } from 'utils/api/sortAccounts';
import KeyboardAccountsRow from './KeyboardAccountsRow';
import { isDesktopOrWeb, isWeb } from '../../../../constants';
const strings = {
    empty: 'There are no accounts connected to your transactions.',
};
export const getAccountsTitle = (count, excluded) => `${excluded ? 'Hidden' : 'Accounts'} (${count})`;
const keyExtractor = (item) => item.type === 'header' ? `${item.id}-${item.excluded}` : item.id.toString();
const KeyboardAccounts = ({ onSelect, onSelectAll, activeFilters, settingsFilter, onRemoveFilter, contentContainerStyle, }) => {
    const styles = useStyles(styleSet);
    const data = useAppSelector(selectActiveAccountsWithHidden);
    const sortedAccounts = useMemo(() => {
        const sorted = sortAccountsByType([...data]);
        const hidden = [];
        const nonHidden = [];
        for (const acc of sorted) {
            if (acc?.isHidden)
                hidden.push(acc);
            else
                nonHidden.push(acc);
        }
        return { hidden, nonHidden };
    }, [data]);
    const { hidden: hiddenAccounts, nonHidden: nonHiddenAccounts } = sortedAccounts;
    const activeFilterMap = useMemo(() => activeFilters?.reduce((acc, curr) => {
        acc[curr.id] = true;
        return acc;
    }, {}) ?? {}, [activeFilters]);
    const selectedAccounts = useMemo(() => {
        const nonHiddenSelected = nonHiddenAccounts.filter((acc) => activeFilterMap[acc.id]);
        const hiddenSelected = hiddenAccounts.filter((acc) => activeFilterMap[acc.id]);
        return {
            nonHidden: nonHiddenSelected,
            hidden: hiddenSelected,
        };
    }, [activeFilterMap, hiddenAccounts, nonHiddenAccounts]);
    const { sectionedAccounts, sectionHeaderIndices, } = useMemo(() => {
        const data = [];
        const headerIndices = [];
        if (nonHiddenAccounts.length > 0) {
            headerIndices.push(data.length);
            data.push({
                type: 'header',
                id: `non-hidden-header`,
                excluded: false,
            });
            data.push(...nonHiddenAccounts);
        }
        if (hiddenAccounts.length > 0) {
            headerIndices.push(data.length);
            data.push({
                type: 'header',
                id: `hidden-header`,
                excluded: true,
            });
            data.push(...hiddenAccounts);
        }
        return { sectionedAccounts: data, sectionHeaderIndices: headerIndices };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [nonHiddenAccounts, hiddenAccounts, selectedAccounts.hidden, selectedAccounts.nonHidden]);
    const sectionHeader = useCallback((excluded) => {
        const accounts = excluded ? hiddenAccounts : nonHiddenAccounts;
        const selected = excluded ? selectedAccounts.hidden : selectedAccounts.nonHidden;
        const isAllSelected = selected.length === accounts.length;
        return (<View style={styles.selectAllRow}>
          <Text Text-16 Secondary>
            {getAccountsTitle(selected.length, excluded)}
          </Text>
          <TextButton text={`${isAllSelected ? 'Deselect' : 'Select'} all`} onPress={() => {
                if (isAllSelected) {
                    const excludedIds = new Set(selected.map((acc) => acc.id));
                    const result = activeFilters?.filter((filter) => !excludedIds.has(filter.id)) || [];
                    if (result.length) {
                        onSelectAll(result);
                    }
                    else {
                        onRemoveFilter('ACCOUNT');
                    }
                }
                else {
                    const newFilters = accounts.map((acc) => ({
                        ...acc,
                        badgeText: acc.name,
                        filterType: 'ACCOUNT',
                    }));
                    const existingIds = new Set(activeFilters?.map((f) => f.id) || []);
                    const filtersToAdd = newFilters.filter((f) => !existingIds.has(f.id));
                    const result = [...(activeFilters || []), ...filtersToAdd];
                    onSelectAll(result);
                }
            }}/>
        </View>);
    }, [
        activeFilters,
        hiddenAccounts,
        nonHiddenAccounts,
        onRemoveFilter,
        onSelectAll,
        selectedAccounts.hidden,
        selectedAccounts.nonHidden,
        styles.selectAllRow,
    ]);
    const type = useMemo(() => (settingsFilter === 'AND' ? 'single' : 'multiple'), [settingsFilter]);
    const renderItem = useCallback(({ item, index }) => {
        if (item.type === 'header') {
            return sectionHeader(item.excluded);
        }
        const isBeforeSectionHeader = sectionedAccounts[index + 1]?.type === 'header';
        return (<KeyboardAccountsRow item={item} type={type} isSelected={activeFilterMap?.[item.id]} isBeforeSectionHeader={isBeforeSectionHeader} onPress={() => {
                onSelect('KeyboardAccounts', {
                    ...item,
                    badgeText: item.name,
                    filterType: 'ACCOUNT',
                });
            }}/>);
    }, [activeFilterMap, onSelect, sectionHeader, sectionedAccounts, type]);
    const flatListStyles = useMarginBottom('paddingBottom', undefined, contentContainerStyle);
    const extraData = useMemo(() => ({ activeFilterMap, selectedHidden: selectedAccounts.hidden, selectedNonHidden: selectedAccounts.nonHidden }), [activeFilterMap, selectedAccounts.hidden, selectedAccounts.nonHidden]);
    const Content = useMemo(() => {
        const baseProps = {
            data: sectionedAccounts,
            renderItem,
            keyExtractor,
            style: styles.flatList,
            contentContainerStyle: flatListStyles,
            ListEmptyComponent: <EmptyComponent />,
            extraData,
        };
        if (isWeb) {
            return <FlatList {...baseProps}/>;
        }
        return <FlashList {...baseProps} stickyHeaderIndices={sectionHeaderIndices}/>;
    }, [extraData, flatListStyles, renderItem, sectionHeaderIndices, sectionedAccounts, styles.flatList]);
    return Content;
};
const EmptyComponent = () => (<View style={staticStyles.emptyContainer}>
    <Text Text-16 centered>
      {strings.empty}
    </Text>
  </View>);
const staticStyles = StyleSheet.create({
    emptyContainer: {
        paddingTop: rem(40),
        paddingHorizontal: rem(40),
    },
});
const styleSet = createStyleSheets((colors) => ({
    flatList: {
        ...(isDesktopOrWeb && {
            padding: 8,
        }),
    },
    row: {
        paddingHorizontal: rem(16),
    },
    selectAllRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: rem(12),
        justifyContent: 'space-between',
        paddingHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && {
            paddingBottom: rem(8),
            paddingRight: rem(4),
        }),
    },
}));
export default KeyboardAccounts;
